import dayjs from 'dayjs';
import {
  stripPropFlags,
  getSideString,
  getSideStringProps,
} from '@/core/utils';
import { formatOdds, formatOddsNoCommission } from '@/core/oddsFormatUtils';

function toFiniteNumber(value) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function formatShareAmount(value) {
  const amount = toFiniteNumber(value);
  if (Math.abs(amount) >= 1000) return Math.round(amount).toString();
  if (Number.isInteger(amount)) return amount.toString();
  return amount.toFixed(2);
}

function getMatchupLabel(game) {
  if (!game) return '';
  if (Array.isArray(game.participants) && game.participants.length >= 2) {
    const first =
      game.participants[0]?.shortName || game.participants[0]?.longName;
    const second =
      game.participants[1]?.shortName || game.participants[1]?.longName;
    return `${first || ''} @ ${second || ''}`.trim();
  }
  return stripPropFlags(game.eventName) || '';
}

function getPickLabel(session, useMediumNames = true) {
  const game = session?.game || {};
  const side =
    game.league && !game.league.includes('PROPS')
      ? getSideString(game, session, true, useMediumNames)
      : getSideStringProps(game, session);
  const odds =
    session?.origin === 'offer'
      ? formatOddsNoCommission(session?.odds)
      : formatOdds(session?.odds, game?.league?.toLowerCase(), session?.type);
  return `${side || ''} ${odds || ''}`.trim();
}

function getStartLabel(startTime) {
  if (!startTime) return '';
  return dayjs(startTime).format('ddd, MMM D · h:mm A');
}

function formatTimeLabel(time) {
  if (!time) return '';
  return dayjs(time).format('MMM D, h:mm A');
}

// Maps a graded wager's `outcome` to the card's win/loss/push vocabulary, and
// to the realized amount. Half-results settle at half the stake/payout.
// Returns null for an ungraded bet, which renders as a pending RISK/TO WIN card.
function gradedResult(session, { risk, win }) {
  switch (session?.outcome) {
    case 'winner':
      return { outcome: 'win', amount: win, typeLabel: 'Graded Win' };
    case 'half-win':
      return { outcome: 'win', amount: win / 2, typeLabel: 'Graded Half Win' };
    case 'loser':
      return { outcome: 'loss', amount: -risk, typeLabel: 'Graded Loss' };
    case 'half-loss':
      return {
        outcome: 'loss',
        amount: -risk / 2,
        typeLabel: 'Graded Half Loss',
      };
    case 'refunded':
      return { outcome: 'push', amount: 0, typeLabel: 'Graded Push' };
    default:
      return null;
  }
}

export function buildSharePayloadFromSession(session, options = {}) {
  const game = session?.game || {};
  const risk =
    options.riskOverride !== undefined
      ? toFiniteNumber(options.riskOverride)
      : toFiniteNumber(session?.bet);
  const win =
    options.winOverride !== undefined ? toFiniteNumber(options.winOverride) : 0;
  const graded = gradedResult(session, { risk, win });
  const accepted = session?.matchedTime || session?.createdAt;
  return {
    id: session?.id || session?.sessionID || null,
    pick: getPickLabel(session, options.useMediumNames !== false),
    matchup: getMatchupLabel(game),
    league: game?.league || '',
    startTime: game?.start || null,
    startLabel: getStartLabel(game?.start),
    // The card shows when the bet was accepted, not when the game starts.
    timeLabel: accepted ? `ACCEPTED ${formatTimeLabel(accepted)}` : '',
    risk,
    win,
    // Present only once graded — the renderer switches to the result layout
    // (WON/LOST/PUSH pill + realized net) when `outcome` is set.
    ...(graded ? { outcome: graded.outcome, amount: graded.amount } : {}),
    typeLabel:
      options.typeLabel ||
      graded?.typeLabel ||
      (session?.origin === 'offer' ? 'Offer' : 'Taken'),
  };
}

export function buildSharePayloadFromCombined(data) {
  return {
    id: data?.id || null,
    pick: data?.pick || '',
    matchup: data?.matchup || '',
    league: data?.league || '',
    startTime: data?.startTime || null,
    startLabel: getStartLabel(data?.startTime),
    timeLabel: data?.acceptedTime
      ? `ACCEPTED ${formatTimeLabel(data.acceptedTime)}`
      : '',
    risk: toFiniteNumber(data?.risk),
    win: toFiniteNumber(data?.win),
    // Callers may opt into the result layout (pill + amount) instead of the
    // RISK/TO WIN tiles — e.g. a hedged position, which wins either way.
    ...(data?.outcome
      ? {
          outcome: data.outcome,
          amount: toFiniteNumber(data.amount),
          ...(data.outcomeLabel ? { outcomeLabel: data.outcomeLabel } : {}),
        }
      : {}),
    typeLabel: data?.typeLabel || 'Combined',
  };
}

export function buildSharePayloadFromGradedPosition(session) {
  const amount = toFiniteNumber(session?.amount);
  // Graded positions from user/getGradedPositions are game-level aggregates:
  // `amount` is the realized net across all wagers on the game, and there is
  // no per-bet odds/stake on the list payload — so the card leads with the
  // matchup + result rather than a pick line.
  const outcome = amount > 0 ? 'win' : amount < 0 ? 'loss' : 'push';
  return {
    id: session?.id || session?.sessionID || null,
    // Prefer the short matchup over the long eventName as the headline
    // fallback — the card renderer dedupes it out of the meta line.
    pick:
      getPickLabel(session, true) ||
      getMatchupLabel(session?.game) ||
      stripPropFlags(session?.game?.eventName) ||
      'Graded Pick',
    matchup: getMatchupLabel(session?.game),
    league: session?.game?.league || '',
    startTime: session?.game?.start || null,
    startLabel: getStartLabel(session?.game?.start),
    timeLabel: formatTimeLabel(session?.updatedAt || session?.game?.start),
    risk: toFiniteNumber(session?.bet),
    // For graded rows, amount is the realized result; share it as "win" value.
    win: Math.abs(amount),
    outcome,
    amount,
    typeLabel:
      amount > 0 ? 'Graded Win' : amount < 0 ? 'Graded Loss' : 'Graded Push',
  };
}

export function buildShareSlipMessage(sharePayload, shareUrl) {
  const resultLine = sharePayload?.outcome
    ? sharePayload.outcome === 'push'
      ? 'Result: push'
      : `${sharePayload.outcome === 'win' ? 'Won' : 'Lost'} ${
          sharePayload.amount > 0 ? '+' : '-'
        }$${formatShareAmount(Math.abs(sharePayload.amount))}`
    : `Risk $${formatShareAmount(sharePayload?.risk)} to win $${formatShareAmount(sharePayload?.win)}`;
  const lines = [
    `Check out this 4casters slip: ${sharePayload?.pick || ''}`.trim(),
    sharePayload?.matchup || '',
    [sharePayload?.league, sharePayload?.timeLabel].filter(Boolean).join(' · '),
    resultLine,
    shareUrl || '',
  ].filter(Boolean);
  return lines.join('\n');
}
